import type React from 'react';
import { useState } from 'react';

import type { ColumnDef, RowSelectionState } from '@tanstack/react-table';
import { useGetStreams } from '@vlabs/api/artemy/client';
import type { GroupId, StreamRecord } from '@vlabs/api/artemy/model';
import { CrossIcon, DeleteIcon } from '@vlabs/icons';
import { BulkActions } from '@vlabs/uikit';
import { Button } from '@vlabs/uikit/components/controls/button';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button';
import { PaginationPanel } from '@vlabs/uikit/components/pagination-v2';
import type { WithClassNameProp } from '@vlabs/uikit/types';
import cn from 'classnames';

import {
  getStreamsParams,
  useSourceFilters,
} from '@vlabs/monokit/entities/sources';
import {
  confirmDeleteStreams,
  DeleteStreamsButton,
} from '@vlabs/monokit/features/delete-stream';
import { useDeleteStreamTranslation } from '@vlabs/monokit/features/delete-stream/i18n';
import { TransferStreamsButton } from '@vlabs/monokit/features/transfer-streams';
import { TableViewSelectable } from '@vlabs/monokit/shared/ui/table-view';
import { TableViewHead } from '@vlabs/monokit/shared/ui/table-view/head';
import type { TableViewHeadProps } from '@vlabs/monokit/shared/ui/table-view/head/types';

import { useSourceGroupListViewerTranslation } from '../i18n';
import st from './StreamListTable.module.sass';

interface GenericStreamListTableProps extends WithClassNameProp {
  groupId: GroupId;
  /** Колонки передаются явно (buildStreamsColumns) — таблица не знает о наборах колонок. */
  columns: ColumnDef<StreamRecord>[];
  /** Потоки с отложенными операциями (удаление/перенос) скрываются из таблицы до «Сохранить». */
  hiddenStreamIds?: Set<string>;
  /** Режим отложенного удаления: подтверждение + очередь вместо API-вызова. */
  onEnqueueDelete?: (streamIds: string[]) => void;
  /** Режим отложенного переноса: выбор целевой группы + очередь вместо API-вызова. */
  onEnqueueTransfer?: (streamIds: string[], targetGroupId: string) => void;
}

function StreamListTableHead({
  table,
  ...props
}: TableViewHeadProps<StreamRecord>): React.JSX.Element {
  const { t } = useSourceGroupListViewerTranslation();
  const { t: tDelete } = useDeleteStreamTranslation();
  const { meta } = table.options;

  const hasSelectedRows = table.getIsSomeRowsSelected() || table.getIsAllRowsSelected();
  const selectedStreamIds = table.getSelectedRowModel().rows
    .map((row) => row.original.stream_id)
    .filter((streamId): streamId is string => !!streamId);
  const clearSelection = () => table.resetRowSelection();

  let bulkActions: React.JSX.Element | null = null;
  if (hasSelectedRows) {
    let deleteControl: React.JSX.Element;
    if (meta?.onEnqueueDelete) {
      deleteControl = (
        <Button
          icon={<DeleteIcon />}
          kind="negative"
          onClick={() => confirmDeleteStreams({
            onConfirm: () => {
              meta?.onEnqueueDelete?.(selectedStreamIds);
              clearSelection();
            },
          })}
          size="small"
          variant="flat-inverted"
        >
          {tDelete('title')}
        </Button>
      );
    } else {
      deleteControl = (
        <DeleteStreamsButton
          onSuccess={clearSelection}
          size="small"
          streamIds={selectedStreamIds}
          variant="flat-inverted"
        />
      );
    }

    bulkActions = (
      <BulkActions
        indeterminate={table.getIsSomeRowsSelected()}
        onCheckboxClick={table.getToggleAllRowsSelectedHandler()}
      >
        <div className={st.BulkActionsContent}>
          <span className={st.SelectedCount}>
            {t('selected_streams', { count: selectedStreamIds.length })}
          </span>
          <div className={st.BulkActionsControls}>
            <TransferStreamsButton
              kind="secondary"
              onSuccess={clearSelection}
              onTransfer={meta?.onEnqueueTransfer ? (targetGroupId) => {
                meta?.onEnqueueTransfer?.(selectedStreamIds, targetGroupId);
              } : undefined}
              size="small"
              streamIds={selectedStreamIds}
              variant="flat-inverted"
            />
            {deleteControl}
            <IconButton
              icon={<CrossIcon />}
              kind="secondary"
              onClick={clearSelection}
              shape="square"
              size="small"
              variant="flat-inverted"
            />
          </div>
        </div>
      </BulkActions>
    );
  }

  return (
    <TableViewHead
      {...props}
      className={st.TableHead}
      table={table}
    >
      {bulkActions}
    </TableViewHead>
  );
}

function GenericStreamListTable({
  groupId,
  columns,
  hiddenStreamIds = undefined,
  onEnqueueDelete = undefined,
  onEnqueueTransfer = undefined,
  className = undefined,
}: GenericStreamListTableProps): React.JSX.Element {
  const { filters, updateFilters } = useSourceFilters();

  const getStreams = useGetStreams(getStreamsParams(filters.group_id ?? groupId, filters));
  const streams = (getStreams.data?.streams ?? []).filter((stream) => (
    !stream.stream_id || !hiddenStreamIds?.has(stream.stream_id)
  ));

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const page = filters.page ?? 1;
  const pageSize = filters.page_size ?? 25;
  // ponytail: бэк не отдаёт total — pageCount оценивается по заполненности страницы
  const canNextPage = streams.length >= pageSize;

  return (
    <div className={cn(st.StreamListTable, className)}>
      <TableViewSelectable
        columns={columns}
        data={streams}
        getRowId={(row, index) => row.stream_id ?? String(index)}
        meta={{ onEnqueueDelete, onEnqueueTransfer }}
        onRowSelectionChange={setRowSelection}
        rowSelection={rowSelection}
        TableHead={StreamListTableHead}
      />

      <div className={st.Pagination}>
        <PaginationPanel
          canNextPage={canNextPage}
          canPreviousPage={page > 1}
          gotoPage={(pageIndex) => updateFilters({ page: pageIndex + 1 })}
          nextPage={() => updateFilters({ page: page + 1 })}
          pageCount={page + (canNextPage ? 1 : 0)}
          pageIndex={page - 1}
          pageSize={pageSize}
          previousPage={() => updateFilters({ page: page - 1 })}
          setPageSize={(page_size: number) => updateFilters({ page: 1, page_size })}
        />
      </div>
    </div>
  );
}

export { GenericStreamListTable };
